import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import '../styles/AddProjectModal.css';

const AddProjectModal = ({ onClose, onProjectAdded }) => {
  const { translations } = useLanguage();
  const { token } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    engineer: '',
    startDate: '',
    endDate: '',
    images: [],
    previews: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conversionProgress, setConversionProgress] = useState({ current: 0, total: 0 });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addImageInput = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, null],
      previews: [...prev.previews, null]
    }));
  };

  const convertToPNG = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          const pngFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.png'), { type: 'image/png' });
          resolve(pngFile);
        }, 'image/png');
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImage = (index) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const pngFile = await convertToPNG(file);
      const newImages = [...formData.images];
      const newPreviews = [...formData.previews];
      newImages[index] = pngFile;
      newPreviews[index] = URL.createObjectURL(pngFile);
      setFormData(prev => ({
        ...prev,
        images: newImages,
        previews: newPreviews
      }));
      setError('');
    } catch (err) {
      setError('Failed to convert image to PNG');
    } finally {
      setLoading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const newPreviews = formData.previews.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      images: newImages,
      previews: newPreviews
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.images.length === 0 || !formData.images[0]) {
      setError('Please select at least one project image');
      return;
    }
    setError('');
    setLoading(true);
    setConversionProgress({ current: 0, total: formData.images.filter(img => img).length });

    try {
      const body = new FormData();
      Object.keys(formData).forEach(k => {
        if (k === 'images' || k === 'previews') return;
        body.append(k, formData[k]);
      });
      formData.images.forEach((img, i) => {
        if (img) body.append('images', img);
      });

const API_URL = process.env.REACT_APP_API_URL || 'https://mammotz-backend.onrender.com';
      console.log('Starting upload...');
      const res = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body
      });

      const data = await res.json();
      console.log('Upload response:', data);
      if (!res.ok) throw new Error(data.message || 'Failed to add project');

      onProjectAdded();
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setConversionProgress({ current: 0, total: 0 });
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } }
  };

  const modalVariants = {
    hidden: { scale: 0.8, opacity: 0, y: 50 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.4
      }
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      y: 50,
      transition: { duration: 0.3 }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="add-project-overlay"
        onClick={onClose}
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <motion.div
          className="add-project-card"
          onClick={e => e.stopPropagation()}
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.button
            className="add-project-close"
            onClick={onClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            ×
          </motion.button>

          <motion.div
            className="add-project-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            {translations.admin.projectModal.title}
          </motion.div>

          <motion.div
            className="add-project-body"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                {error}
              </motion.div>
            )}

            <form className="add-project-form" onSubmit={handleSubmit}>
              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
              >
                <label>{translations.admin.projectModal.projectName}</label>
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                <label>{translations.admin.projectModal.description}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  disabled={loading}
                />
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.3 }}
              >
                <label>{translations.admin.projectModal.engineer}</label>
                <input
                  name="engineer"
                  value={formData.engineer}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, duration: 0.3 }}
              >
                <label>{translations.admin.projectModal.startDate}</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.3 }}
              >
                <label>{translations.admin.projectModal.endDate}</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </motion.div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, duration: 0.3 }}
              >
                <label>{translations.admin.projectModal.image}</label>
                {formData.images.map((_, index) => (
                  <motion.div
                    key={index}
                    className="image-input-group"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.0 + index * 0.1, duration: 0.3 }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImage(index)}
                      disabled={loading}
                    />
                    {formData.previews[index] && (
                      <motion.div
                        className="image-preview-container"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <img src={formData.previews[index]} alt="" className="image-preview" />
                        <motion.button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeImage(index)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          ×
                        </motion.button>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
                <motion.button
                  type="button"
                  className="add-image-btn"
                  onClick={addImageInput}
                  disabled={loading}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  + Add Another Image
                </motion.button>
              </motion.div>

              {loading && conversionProgress.total > 0 && (
                <motion.div
                  className="conversion-progress"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="progress-bar">
                    <motion.div
                      className="progress-fill"
                      style={{ width: `${(conversionProgress.current / conversionProgress.total) * 100}%` }}
                      initial={{ width: 0 }}
                      animate={{ width: `${(conversionProgress.current / conversionProgress.total) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    ></motion.div>
                  </div>
                  <p>Converting images to PNG... ({conversionProgress.current}/{conversionProgress.total})</p>
                </motion.div>
              )}

              <motion.button
                type="submit"
                className="submit-btn"
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                {loading ? (conversionProgress.total > 0 ? 'Converting Images...' : 'Uploading...') : translations.admin.projectModal.submit}
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddProjectModal;